import {
  Autocomplete,
  Box,
  Button,
  Grid,
  IconButton,
  InputAdornment,
  ListSubheader,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import FlexBetween from "../../components/FlexBetween";
import { useToast } from "../../services/utils";
import {
  addEditConnectionKeySchema,
  addEditConnectionKeyUnifiedSchema,
} from "../../schemas";
import {
  addConnectionKey,
  testConnectionKey,
  updateConnectionKey,
  decryptConnectionKeyField,
} from "../../services/connectionKeysService";
import Visibility from "@mui/icons-material/VisibilityOutlined";
import useFetchConnectionKeyById from "../../hooks/useFetchConnectionKeyById";
import Loader from "../../components/Molecules/Loader/Loader";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import ViewJsonSplitButton from "../../components/common/ViewJsonSplitButton";
import CopyButton from "../../components/common/CopyButton";
import {
  CONNECTION_KEY_SERVICES_GROUPED,
  CONNECTION_KEY_SERVICES_TYPE,
  CONNECTION_KEY_SERVICES_TYPES,
} from "../../services/constants/ConnectionKey";
import { useBreadCrumbContext } from "../../contexts/BreadCrumbContext";
import { useSystemContext } from "../../contexts/TagAndSystemProvider";
import useFetchAllSystem from "../../hooks/useFetchAllSystem";
import useFetchAllConnectionRuntimeConfigs from "../../hooks/useFetchAllConnectionRuntimeConfigs";
import {
  AZURE_SQL_CONNECTION_STRING_SYNTAX_HELP_TEXT,
  AZURE_SQL_SERVICE_PRINCIPAL_CONNECTION_STRING_SYNTAX_HELP_TEXT,
  BLOB_CONNECTION_STRING_SYNTAX_HELP_TEXT,
  MARIADB_CONNECTION_STRING_SYNTAX_HELP_TEXT,
  ORACLE_SQL_CONNECTION_STRING_SYNTAX_HELP_TEXT,
  POSTGRES_SQL_CONNECTION_STRING_SYNTAX_HELP_TEXT,
  SNOFLAKE_SQL_CONNECTION_STRING_SYNTAX_HELP_TEXT,
} from "../../constants/connectionStringTemplates";
import { getDifferentValues } from "../../services/utils/processRuleExecutionResourcesData";
import CommentBeforeUpdateDialog from "../../components/Dialogs/CommentBeforeUpdateDialog";
import ViewPayloadDialog from "../../components/Dialogs/ViewPayloadDialog";
import { validateConfigRequest } from "../../services/resourcesService";
import { useAuth } from "../../contexts/AuthContext";

const AddConnectionKey: React.FC = () => {
  const { setCurrentBreadcrumbPage } = useBreadCrumbContext();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { id: connectionKeyId } = useParams();
  const [formData, setFormData] = useState<any>({
    name: "",
    type: CONNECTION_KEY_SERVICES_TYPES[0] || "",
    system_id: null,
    ip_address: "",
    user_name: "",
    password: "",
    container_name: "",
    connection_string: "",
    client_id: "",
    client_secret: "",
    tenant_id: "",
    api_url: "",
    azure_tenant_id: "",
    azure_client_id: "",
    azure_client_secret: "",
    database_uri: "",
    connection_runtime_config_id: null,
  });
  const { isAdmin } = useAuth();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isKeyTested, setIsKeyTested] = useState<boolean>(false);
  const [initialFormData, setInitialFormData] = useState<any>({});
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const isEditMode = Boolean(connectionKeyId);
  const [isPayloadDialogOpen, setIsPayloadDialogOpen] = useState(false);
  const [validationResponse, setValidationResponse] = useState<any>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [openCommentConfirmation, setOpenCommentConfirmation] =
    useState<boolean>(false);
  const [comment, setComment] = useState<string>("");
  const [pendingConnectionKeyPayload, setPendingConnectionKeyPayload] =
    useState<any>(null);

  // State for sensitive field visibility and decrypted values
  const [showSensitiveFields, setShowSensitiveFields] = useState<{
    [key: string]: boolean;
  }>({
    password: false,
    client_secret: false,
    azure_client_secret: false,
    azure_client_id: false,
    azure_tenant_id: false,
    token: false,
    api_key: false,
    connection_string: false,
  });

  // hooks
  const [connectionKeyData] = useFetchConnectionKeyById({
    setIsLoading,
    currentConnectionKeyId: connectionKeyId,
  });
  // Get the setter function from context (following linkedServicesData pattern)
  const { allSystemData, setAllSystemData } = useSystemContext();

  // Fetch system data directly in this component (following linkedServicesData pattern)
  const [systemData] = useFetchAllSystem({
    setIsLoading,
  });

  // Fetch connection runtime configs only when type is SFTP
  const [connectionRuntimeConfigs] = useFetchAllConnectionRuntimeConfigs({
    setIsLoading,
    shouldFetch: formData?.type === "sftp",
  });

  // Update context state when system data is fetched (following linkedServicesData pattern)
  useEffect(() => {
    if (systemData) {
      setAllSystemData(systemData);
    }
  }, [systemData, setAllSystemData]);

  useEffect(() => {
    if (formData) {
      setIsKeyTested(false);
    }
  }, [formData]);

  // Track form changes
  useEffect(() => {
    // Only check for changes if initialFormData is properly set
    if (Object.keys(initialFormData).length > 0) {
      const hasFormChanges =
        JSON.stringify(formData) !== JSON.stringify(initialFormData);
      setHasChanges(hasFormChanges);
    } else {
      // If initialFormData is not set yet, disable save button
      setHasChanges(false);
    }
  }, [formData, initialFormData]);

  const handleFormData = (e: any) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });

    validateField(e.target.name, e.target.value);
  };
  const handelOnChangeType = (e: any) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
      ip_address: "",
      user_name: "",
      password: "",
      container_name: "",
      connection_string: "",
      client_id: "",
      client_secret: "",
      tenant_id: "",
      api_url: "",
      azure_tenant_id: "",
      azure_client_id: "",
      azure_client_secret: "",
      database_uri: "",
      connection_runtime_config_id: null,
    });
  };
  const handleConnectionRuntimeConfigChange = (value: any) => {
    setFormData({
      ...formData,
      connection_runtime_config_id: value?.id || null,
    });
    validateField("connection_runtime_config_id", value?.id || null);
  };

  const validateField = async (name: any, value: any) => {
    try {
      // Create a partial form data object with only the field being changed
      const partialFormData = { ...formData, [name]: value };
      // Use the unified schema with conditional validation based on type
      await addEditConnectionKeyUnifiedSchema.validateAt(name, partialFormData);
      // If validation passes, clear any previous errors for this field
      setErrors((prevErrors) => ({ ...prevErrors, [name]: undefined }));
    } catch (validationError: any) {
      // If validation fails, set the error message for this field
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: validationError.message,
      }));
    }
  };

  // Handle visibility toggle for sensitive fields
  const handleToggleSensitiveField = async (fieldName: string) => {
    // One-way action: decrypt and show only, no toggle back
    try {
      setIsLoading(true);
      const response = await decryptConnectionKeyField(formData[fieldName]);
      // Update formData with the decrypted value
      setFormData((prev: any) => ({
        ...prev,
        [fieldName]: response ?? "",
      }));

      // Update visibility state (one-way, cannot toggle back)
      setShowSensitiveFields((prev) => ({
        ...prev,
        [fieldName]: true,
      }));

      setIsLoading(false);
    } catch (error) {
      console.error("Error decrypting field:", error);
      showToast("Failed to decrypt field", "error");
      setIsLoading(false);
    }
  };

  const authAPINames = [
    "no_auth_api",
    "basic_auth_api",
    "token_auth_api",
    "key_auth_api",
    "oauth_api",
  ];
  const modifiedApiDetails = (formData: any) => {
    if (authAPINames.includes(formData?.type)) {
      const apiDetails = {
        url: formData?.api_url ?? null,
        username: formData?.user_name ?? null,
        password: formData?.password ?? null,
        token: formData?.token ?? null,
        api_key: formData?.api_key ?? null,
      };
      const modifiedFormData = {
        ...formData,
        api_details: apiDetails,
      };
      ["api_url", "user_name", "password", "token", "api_key"].forEach((e) => {
        delete modifiedFormData[e];
      });
      return modifiedFormData;
    }
    return formData;
  };

  // handel event when save a connection key
  const onSaveConnectionKey = async (e: any) => {
    e.preventDefault();
    try {
      // Use the unified schema with conditional validation
      await addEditConnectionKeyUnifiedSchema.validate(formData, {
        abortEarly: false,
      });

      let payload;
      if (isEditMode) {
        // In edit mode, use differential comparison to send only changed fields
        const differentValues = getDifferentValues(formData, initialFormData);

        // If no fields have changed, show a message and skip the API call
        if (Object.keys(differentValues).length === 0) {
          showToast("No changes detected to update", "warning");
          return;
        }

        payload = differentValues;
        // Show comment dialog before updating
        setPendingConnectionKeyPayload(payload);
        setOpenCommentConfirmation(true);
      } else {
        // In add mode, send all fields directly
        const reqBody = { ...formData, is_active: true };
        payload = Object.fromEntries(
          Object.entries(reqBody).filter(([key, value]) => value !== ""),
        );
        addConnectionKey({
          currentConnectionKeyId: connectionKeyId,
          payload: payload,
        })
          .then(() => {
            showToast("Connection Key created successfully!", "success");
            navigate("/connection-keys");
          })
          .catch((error) => {
            console.error("Cannot create connection key");
          });
      }
    } catch (validationErrors: any) {
      const newErrors: { [key: string]: string } = {};
      if (validationErrors && validationErrors.inner) {
        validationErrors.inner.forEach(
          (error: { path: string | number; message: string }) => {
            newErrors[error.path] = error.message;
          },
        );
      }
      setErrors((prevErrors) => ({
        ...prevErrors,
        ...newErrors,
      }));
    }
  };

  const handleCommentChange = (e: any) => {
    setComment(e.target.value);
  };

  const handleSaveComment = () => {
    setOpenCommentConfirmation(false);
    updateConnectionKey({
      currentConnectionKeyId: connectionKeyId,
      payload: { ...pendingConnectionKeyPayload, comment },
    })
      .then(() => {
        showToast("Connection Key updated successfully!", "success");
        navigate("/connection-keys");
      })
      .catch((error) => {
        console.error("Cannot update connection key");
      });
    setComment("");
    setPendingConnectionKeyPayload(null);
  };

  const handleCancelComment = () => {
    setOpenCommentConfirmation(false);
    setComment("");
    setPendingConnectionKeyPayload(null);
  };

  const buildPayload = () => {
    // View/Copy must remain useful before the user edits a field. A differential
    // edit payload is empty on initial load, which rendered a blank JSON dialog.
    const reqBody = isEditMode
      ? { ...formData }
      : { ...formData, is_active: true };
    return Object.fromEntries(
      Object.entries(reqBody).filter(([_, value]) => value !== ""),
    );
  };

  const handleValidatePayload = async (payload: any) => {
    setIsValidating(true);
    try {
      const validateReqBody = {
        target_endpoint: isEditMode
          ? `/connection-key/${connectionKeyId}`
          : "/connection-key",
        method: isEditMode ? "PUT" : "POST",
        body: payload,
        query_params: {},
        url_params: isEditMode ? { id: connectionKeyId } : {},
      };
      const response = await validateConfigRequest(validateReqBody);
      setValidationResponse(response);
      if (response?.is_valid) {
        showToast("Payload is valid!", "success");
      }
    } catch (error: any) {
      showToast(error?.response?.data?.message || "Validation failed", "error");
    } finally {
      setIsValidating(false);
    }
  };

  const handleCopyPayload = async () => {
    const payload = buildPayload();
    await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
    showToast("Payload copied to clipboard!", "success");
  };

  const handleKeyTesting = async () => {
    let modifiedFormData = { ...formData };
    try {
      setIsLoading(true);
      // Use the unified schema with conditional validation
      await addEditConnectionKeyUnifiedSchema.validate(formData, {
        abortEarly: false,
      });
      if (authAPINames.includes(formData?.type)) {
        modifiedFormData = modifiedApiDetails(formData);
      }
      const cleanFormData = Object.fromEntries(
        Object.entries(authAPINames ? modifiedFormData : formData).filter(
          ([key, value]) => value !== "",
        ),
      );

      testConnectionKey({ payload: cleanFormData })
        .then((response) => {
          if (response?.status) {
            showToast(
              `Connection Key "${formData.type}" tested successfully!`,
              "success",
            );
            setIsKeyTested(true);
            setIsLoading(false);
          } else {
            showToast(
              `Test for Connection Key "${formData.type}" failed.`,
              "error",
            );
            setIsLoading(false);
          }
        })
        .catch((error) => {
          console.error(
            `Cannot ${connectionKeyId ? "update" : "create"} connection key`,
          );
          setIsLoading(false);
        });
    } catch (validationErrors: any) {
      setIsLoading(false);
      const newErrors: { [key: string]: string } = {};
      validationErrors.inner.forEach(
        (error: { path: string | number; message: string }) => {
          newErrors[error.path] = error.message;
        },
      );
      setErrors((prevErrors) => ({
        ...prevErrors,
        ...newErrors,
      }));
    }
  };

  const copyTextFallback = (text: string) => {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.style.position = "fixed"; // avoid scrolling
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    document.execCommand("copy");
    document.body.removeChild(textarea);
  };

  const handleCopyConnectionString = async () => {
    try {
      let textToCopy = "";

      if (formData.type === "blob") {
        textToCopy = BLOB_CONNECTION_STRING_SYNTAX_HELP_TEXT.string;
      } else if (formData.type === "mariadb") {
        textToCopy = MARIADB_CONNECTION_STRING_SYNTAX_HELP_TEXT.string;
      } else if (formData.type === "azure_sql") {
        textToCopy = AZURE_SQL_CONNECTION_STRING_SYNTAX_HELP_TEXT.string;
      } else if (formData.type === "oracle_sql") {
        textToCopy = ORACLE_SQL_CONNECTION_STRING_SYNTAX_HELP_TEXT.string;
      } else if (formData.type === "snowflake_sql") {
        textToCopy = SNOFLAKE_SQL_CONNECTION_STRING_SYNTAX_HELP_TEXT.string;
      } else if (formData.type === "postgres_sql") {
        textToCopy = POSTGRES_SQL_CONNECTION_STRING_SYNTAX_HELP_TEXT.string;
      } else if (formData.type === "azure_sql_service_principal") {
        textToCopy =
          AZURE_SQL_SERVICE_PRINCIPAL_CONNECTION_STRING_SYNTAX_HELP_TEXT.string;
      }

      if (!textToCopy) return;

      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(textToCopy);
      } else {
        copyTextFallback(textToCopy);
      }

      showToast("Connection string copied to clipboard!", "success");
    } catch (err) {
      console.error("Failed to copy connection string:", err);
      showToast("Failed to copy connection string", "error");
    }
  };

  useEffect(() => {
    if (connectionKeyData?.name) {
      const fetchedData = {
        name: connectionKeyData?.name,
        type: connectionKeyData?.type,
        system_id: connectionKeyData?.system_id,
        ip_address: connectionKeyData?.ip_address,
        user_name: connectionKeyData?.user_name,
        password: connectionKeyData?.password,
        container_name: connectionKeyData?.container_name,
        connection_string: connectionKeyData?.connection_string,
        client_id: connectionKeyData?.client_id,
        client_secret: connectionKeyData?.client_secret,
        tenant_id: connectionKeyData?.tenant_id,
        api_url: connectionKeyData?.api_url,
        azure_tenant_id: connectionKeyData?.azure_tenant_id,
        azure_client_id: connectionKeyData?.azure_client_id,
        azure_client_secret: connectionKeyData?.azure_client_secret,
        database_uri: connectionKeyData?.database_uri,
        connection_runtime_config_id:
          connectionKeyData?.connection_runtime_config_id,
        code: connectionKeyData?.code,
      };
      setFormData(fetchedData);
      // Store initial form data for differential comparison in edit mode
      if (isEditMode) {
        setInitialFormData(JSON.parse(JSON.stringify(fetchedData)));
      }
    }
    setCurrentBreadcrumbPage({
      name: connectionKeyData?.name,
      id: connectionKeyData?.id,
    });
    return () => {
      setCurrentBreadcrumbPage({ name: "", id: null });
    };
  }, [connectionKeyData, isEditMode]);
  useEffect(() => {
    const formChanged =
      JSON.stringify(formData) !== JSON.stringify(initialFormData);
    setHasChanges(formChanged);
  }, [formData, initialFormData]);

  const connectionStringField = () => {
    return (
      <>
        <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
          <label className="label-text">
            Connection String
            <span className="required-asterisk">*</span>
          </label>
          <Box
            className={`textarea-with-msg ${
              formData.type === "blob" ||
              formData.type === "mariadb" ||
              formData.type === "azure_sql"
                ? "has-msg"
                : ""
            }`}
          >
            <TextField
              multiline
              rows={4} // You can adjust this number based on how tall you want the textarea
              name="connection_string"
              onChange={handleFormData}
              className={`form-control ${
                isEditMode && !showSensitiveFields.connection_string
                  ? "withAdornment password-box form-control-with-adornment"
                  : ""
              } ${errors?.connection_string ? "has-error" : ""}`}
              onBlur={(e) => validateField(e.target.name, e.target.value)}
              error={!!errors?.connection_string}
              //helperText={errors?.connection_string || ""}
              value={
                isEditMode && !showSensitiveFields.connection_string
                  ? formData?.connection_string
                    ? "•".repeat(formData.connection_string.length)
                    : ""
                  : formData?.connection_string || ""
              }
              InputProps={{
                // readOnly:
                //   isEditMode &&
                //   !showSensitiveFields.connection_string &&
                //   formData?.connection_string,
                endAdornment:
                  isEditMode && !showSensitiveFields.connection_string ? (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle connection_string visibility"
                        onClick={() =>
                          handleToggleSensitiveField("connection_string")
                        }
                        edge="end"
                      >
                        <Visibility />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
              }}
            />
            {(formData.type === "blob" ||
              formData.type === "mariadb" ||
              formData.type === "azure_sql" ||
              formData.type === "oracle_sql" ||
              formData.type === "snowflake_sql" ||
              formData.type === "postgres_sql" ||
              formData.type === "azure_sql_service_principal") && (
              <Box className="msg-box">
                <code>
                  {formData.type === "blob" ? (
                    <>
                      <p>
                        <strong> Ex: </strong>{" "}
                        {BLOB_CONNECTION_STRING_SYNTAX_HELP_TEXT.string}
                        <CopyButton
                          onClick={handleCopyConnectionString}
                          tooltipTitle="Copy connection string to clipboard"
                        />
                      </p>{" "}
                      <i>{BLOB_CONNECTION_STRING_SYNTAX_HELP_TEXT.helpText}</i>
                    </>
                  ) : formData.type === "mariadb" ? (
                    <>
                      <p>
                        <strong> Ex: </strong>{" "}
                        {MARIADB_CONNECTION_STRING_SYNTAX_HELP_TEXT.string}
                        <CopyButton
                          onClick={handleCopyConnectionString}
                          tooltipTitle="Copy connection string to clipboard"
                        />
                      </p>{" "}
                      <i>
                        {MARIADB_CONNECTION_STRING_SYNTAX_HELP_TEXT.helpText}
                      </i>
                    </>
                  ) : formData.type === "azure_sql" ? (
                    <>
                      <p>
                        <strong> Ex: </strong>{" "}
                        {AZURE_SQL_CONNECTION_STRING_SYNTAX_HELP_TEXT.string}
                        <CopyButton
                          onClick={handleCopyConnectionString}
                          tooltipTitle="Copy connection string to clipboard"
                        />
                      </p>{" "}
                      <i>
                        {AZURE_SQL_CONNECTION_STRING_SYNTAX_HELP_TEXT.helpText}
                      </i>
                    </>
                  ) : formData.type === "oracle_sql" ? (
                    <>
                      <p>
                        <strong> Ex: </strong>{" "}
                        {ORACLE_SQL_CONNECTION_STRING_SYNTAX_HELP_TEXT.string}
                        <CopyButton
                          onClick={handleCopyConnectionString}
                          tooltipTitle="Copy connection string to clipboard"
                        />
                      </p>{" "}
                      <i>
                        {ORACLE_SQL_CONNECTION_STRING_SYNTAX_HELP_TEXT.helpText}
                      </i>
                    </>
                  ) : formData.type === "snowflake_sql" ? (
                    <>
                      <p>
                        <strong> Ex: </strong>{" "}
                        {SNOFLAKE_SQL_CONNECTION_STRING_SYNTAX_HELP_TEXT.string}
                        <CopyButton
                          onClick={handleCopyConnectionString}
                          tooltipTitle="Copy connection string to clipboard"
                        />
                      </p>{" "}
                      <i>
                        {
                          SNOFLAKE_SQL_CONNECTION_STRING_SYNTAX_HELP_TEXT.helpText
                        }
                      </i>
                    </>
                  ) : formData.type === "postgres_sql" ? (
                    <>
                      <p>
                        <strong> Ex: </strong>{" "}
                        {POSTGRES_SQL_CONNECTION_STRING_SYNTAX_HELP_TEXT.string}
                        <CopyButton
                          onClick={handleCopyConnectionString}
                          tooltipTitle="Copy connection string to clipboard"
                        />
                      </p>{" "}
                      <i>
                        {
                          POSTGRES_SQL_CONNECTION_STRING_SYNTAX_HELP_TEXT.helpText
                        }
                      </i>
                    </>
                  ) : formData.type === "azure_sql_service_principal" ? (
                    <>
                      <p>
                        <strong> Ex: </strong>
                        {
                          AZURE_SQL_SERVICE_PRINCIPAL_CONNECTION_STRING_SYNTAX_HELP_TEXT.string
                        }
                        <CopyButton
                          onClick={handleCopyConnectionString}
                          tooltipTitle="Copy connection string to clipboard"
                        />
                      </p>{" "}
                      <i>
                        {
                          AZURE_SQL_SERVICE_PRINCIPAL_CONNECTION_STRING_SYNTAX_HELP_TEXT.helpText
                        }
                      </i>
                    </>
                  ) : (
                    ""
                  )}
                </code>
              </Box>
            )}
          </Box>
          {errors?.connection_string && (
            <p className="validation-error ml-0">{errors?.connection_string}</p>
          )}
        </Grid>
      </>
    );
  };
  const blobFields = () => {
    return (
      <>
        <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
          <label className="label-text">Container Name</label>
          <TextField
            type="text"
            // placeholder="Ex: Sample Container Name"
            name="container_name"
            onChange={handleFormData}
            className={`form-control`}
            value={formData?.container_name}
          />
        </Grid>
        {connectionStringField()}
      </>
    );
  };
  const sftpFields = () => {
    return (
      <>
        <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
          <label className="label-text">
            IP Address<span className="required-asterisk">*</span>
          </label>
          <TextField
            type="text"
            // placeholder="Ex: Sample Connection Keys IP Address"
            name="ip_address"
            onChange={handleFormData}
            className={`form-control ${errors?.ip_address ? "has-error" : ""}`}
            onBlur={(e) => validateField(e.target.name, e.target.value)}
            error={!!errors?.ip_address}
            helperText={errors?.ip_address || " "}
            value={formData?.ip_address}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
          <label className="label-text">
            Username<span className="required-asterisk">*</span>
          </label>
          <TextField
            type="text"
            // placeholder="Ex: Sample Connection Keys Username"
            name="user_name"
            onChange={handleFormData}
            className={`form-control ${errors?.user_name ? "has-error" : ""}`}
            onBlur={(e) => validateField(e.target.name, e.target.value)}
            error={!!errors?.user_name}
            helperText={errors?.user_name || " "}
            value={formData?.user_name}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
          <label className="label-text">
            Password<span className="required-asterisk">*</span>
          </label>
          <TextField
            type={showSensitiveFields.password ? "text" : "password"}
            name="password"
            onChange={handleFormData}
            className={`form-control ${
              isEditMode && !showSensitiveFields.password
                ? "withAdornment password-box form-control-with-adornment"
                : ""
            } ${errors?.password ? "has-error" : ""}`}
            onBlur={(e) => validateField(e.target.name, e.target.value)}
            error={!!errors?.password}
            helperText={errors?.password || " "}
            value={formData?.password || ""}
            InputProps={{
              endAdornment:
                isEditMode && !showSensitiveFields.password ? (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => handleToggleSensitiveField("password")}
                      edge="end"
                    >
                      <Visibility />
                    </IconButton>
                  </InputAdornment>
                ) : null,
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
          <label className="label-text">
            Connection Runtime Config
            <span className="required-asterisk">*</span>
          </label>
          <Autocomplete
            options={connectionRuntimeConfigs}
            getOptionLabel={(option: any) =>
              option?.id && option?.code ? `${option.code}` : ""
            }
            value={
              connectionRuntimeConfigs.find(
                (config: any) =>
                  config?.id === formData?.connection_runtime_config_id,
              ) || null
            }
            onChange={(event, value) =>
              handleConnectionRuntimeConfigChange(value)
            }
            renderInput={(params) => (
              <TextField
                {...params}
                className={`form-control-autocomplete ${
                  errors?.connection_runtime_config_id ? "has-error" : ""
                }`}
                error={!!errors?.connection_runtime_config_id}
                helperText={errors?.connection_runtime_config_id || " "}
              />
            )}
          />
        </Grid>
      </>
    );
  };
  const basicAuthFields = () => {
    return (
      <>
        <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
          <label className="label-text">Username</label>
          <TextField
            type="text"
            name="user_name"
            onChange={handleFormData}
            className={`form-control ${errors?.user_name ? "has-error" : ""}`}
            onBlur={(e) => validateField(e.target.name, e.target.value)}
            error={!!errors?.user_name}
            helperText={errors?.user_name || " "}
            value={formData?.user_name}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
          <label className="label-text">Password</label>
          <TextField
            type={showSensitiveFields.password ? "text" : "password"}
            name="password"
            onChange={handleFormData}
            className={`form-control ${
              isEditMode && !showSensitiveFields.password
                ? "withAdornment password-box form-control-with-adornment"
                : ""
            } ${errors?.password ? "has-error" : ""}`}
            onBlur={(e) => validateField(e.target.name, e.target.value)}
            error={!!errors?.password}
            helperText={errors?.password || " "}
            value={formData?.password || ""}
            InputProps={{
              endAdornment:
                isEditMode && !showSensitiveFields.password ? (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => handleToggleSensitiveField("password")}
                      edge="end"
                    >
                      <Visibility />
                    </IconButton>
                  </InputAdornment>
                ) : null,
            }}
          />
        </Grid>
      </>
    );
  };

  const tokenAuthFields = () => {
    return (
      <>
        <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
          <label className="label-text">
            Token<span className="required-asterisk">*</span>
          </label>
          <TextField
            type={showSensitiveFields.token ? "text" : "password"}
            name="token"
            onChange={handleFormData}
            className={`form-control ${
              isEditMode && !showSensitiveFields.token
                ? "withAdornment password-box form-control-with-adornment"
                : ""
            } ${errors?.token ? "has-error" : ""}`}
            onBlur={(e) => validateField(e.target.name, e.target.value)}
            error={!!errors?.token}
            helperText={errors?.token || " "}
            value={formData?.token || ""}
            InputProps={{
              endAdornment:
                isEditMode && !showSensitiveFields.token ? (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle token visibility"
                      onClick={() => handleToggleSensitiveField("token")}
                      edge="end"
                    >
                      <Visibility />
                    </IconButton>
                  </InputAdornment>
                ) : null,
            }}
          />
        </Grid>
      </>
    );
  };
  const keyAuthFields = () => {
    return (
      <>
        <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
          <label className="label-text">
            API Key<span className="required-asterisk">*</span>
          </label>
          <TextField
            type={showSensitiveFields.api_key ? "text" : "password"}
            name="api_key"
            onChange={handleFormData}
            className={`form-control ${
              isEditMode && !showSensitiveFields.api_key
                ? "withAdornment password-box form-control-with-adornment"
                : ""
            } ${errors?.api_key ? "has-error" : ""}`}
            onBlur={(e) => validateField(e.target.name, e.target.value)}
            error={!!errors?.api_key}
            helperText={errors?.api_key || " "}
            value={formData?.api_key || ""}
            InputProps={{
              endAdornment:
                isEditMode && !showSensitiveFields.api_key ? (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle api_key visibility"
                      onClick={() => handleToggleSensitiveField("api_key")}
                      edge="end"
                    >
                      <Visibility />
                    </IconButton>
                  </InputAdornment>
                ) : null,
            }}
          />
        </Grid>
      </>
    );
  };

  const apiFields = () => {
    return (
      <>
        <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
          <label className="label-text">
            Api Url
            <span className="required-asterisk">*</span>
          </label>
          <TextField
            type="text"
            placeholder="Ex: Sample Api Url"
            name="api_url"
            onChange={handleFormData}
            className={`form-control ${errors?.api_url ? "has-error" : ""}`}
            onBlur={(e) => validateField(e.target.name, e.target.value)}
            error={!!errors?.api_url}
            helperText={errors?.api_url || " "}
            value={formData?.api_url}
          />
        </Grid>
      </>
    );
  };

  const sqlFields = () => {
    return (
      <>
        <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
          <label className="label-text">Database Uri</label>
          <TextField
            type="text"
            name="database_uri"
            onChange={handleFormData}
            className={`form-control`}
            value={formData?.database_uri}
          />
        </Grid>
        {connectionStringField()}
      </>
    );
  };

  const azureSqlServicePrincipalFields = () => {
    return (
      <>
        <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
          <label className="label-text">
            Client Id
            <span className="required-asterisk">*</span>
          </label>
          <TextField
            type={showSensitiveFields.azure_client_id ? "text" : "password"}
            name="azure_client_id"
            onChange={handleFormData}
            className={`form-control ${
              isEditMode && !showSensitiveFields.azure_client_id
                ? "withAdornment password-box form-control-with-adornment"
                : ""
            } ${errors?.azure_client_id ? "has-error" : ""}`}
            onBlur={(e) => validateField(e.target.name, e.target.value)}
            error={!!errors?.azure_client_id}
            helperText={errors?.azure_client_id || " "}
            value={formData?.azure_client_id || ""}
            InputProps={{
              endAdornment:
                isEditMode && !showSensitiveFields.azure_client_id ? (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle azure_client_id visibility"
                      onClick={() =>
                        handleToggleSensitiveField("azure_client_id")
                      }
                      edge="end"
                    >
                      <Visibility />
                    </IconButton>
                  </InputAdornment>
                ) : null,
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
          <label className="label-text">
            Client Secret
            <span className="required-asterisk">*</span>
          </label>
          <TextField
            type={showSensitiveFields.azure_client_secret ? "text" : "password"}
            name="azure_client_secret"
            onChange={handleFormData}
            className={`form-control ${
              isEditMode && !showSensitiveFields.azure_client_secret
                ? "withAdornment password-box form-control-with-adornment"
                : ""
            } ${errors?.azure_client_secret ? "has-error" : ""}`}
            onBlur={(e) => validateField(e.target.name, e.target.value)}
            error={!!errors?.azure_client_secret}
            helperText={errors?.azure_client_secret || " "}
            value={formData?.azure_client_secret || ""}
            InputProps={{
              endAdornment:
                isEditMode && !showSensitiveFields.azure_client_secret ? (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle azure_client_secret visibility"
                      onClick={() =>
                        handleToggleSensitiveField("azure_client_secret")
                      }
                      edge="end"
                    >
                      <Visibility />
                    </IconButton>
                  </InputAdornment>
                ) : null,
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
          <label className="label-text">
            Tenant Id
            <span className="required-asterisk">*</span>
          </label>
          <TextField
            type={showSensitiveFields.azure_tenant_id ? "text" : "password"}
            name="azure_tenant_id"
            onChange={handleFormData}
            className={`form-control ${
              isEditMode && !showSensitiveFields.azure_tenant_id
                ? "withAdornment password-box form-control-with-adornment"
                : ""
            } ${errors?.azure_tenant_id ? "has-error" : ""}`}
            onBlur={(e) => validateField(e.target.name, e.target.value)}
            error={!!errors?.azure_tenant_id}
            helperText={errors?.azure_tenant_id || " "}
            value={formData?.azure_tenant_id || ""}
            InputProps={{
              endAdornment:
                isEditMode && !showSensitiveFields.azure_tenant_id ? (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle azure_tenant_id visibility"
                      onClick={() =>
                        handleToggleSensitiveField("azure_tenant_id")
                      }
                      edge="end"
                    >
                      <Visibility />
                    </IconButton>
                  </InputAdornment>
                ) : null,
            }}
          />
        </Grid>
      </>
    );
  };

  return (
    <>
      {isLoading && <Loader isLoading={isLoading} />}

      <Box>
        <form onSubmit={onSaveConnectionKey} autoComplete="off">
          <Box className="text-box-card-white m-0">
            <Box className="text-box-header">
              <h3>{connectionKeyId ? "Edit " : "Add "} Connection Key</h3>
            </Box>
            <Grid container rowSpacing={1.5} columnSpacing={2.5}>
              <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
                <label className="label-text">
                  Name<span className="required-asterisk">*</span>
                </label>
                <TextField
                  type="text"
                  // placeholder="Ex: Sample Connection Keys Name"
                  name="name"
                  onChange={handleFormData}
                  className={`form-control ${errors?.name ? "has-error" : ""}`}
                  onBlur={(e) => validateField(e.target.name, e.target.value)}
                  error={!!errors?.name}
                  helperText={errors?.name || " "}
                  value={formData?.name}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
                <label className="label-text">
                  Code <span className="required-asterisk">*</span>
                </label>
                <TextField
                  type="text"
                  name="code"
                  onChange={(e) => {
                    if (!connectionKeyData?.code) {
                      handleFormData(e);
                    }
                  }}
                  className={`form-control ${errors?.code ? "has-error" : ""}`}
                  onBlur={(e) => {
                    if (!connectionKeyData?.code) {
                      validateField(e.target.name, e.target.value);
                    }
                  }}
                  error={!!errors?.code}
                  helperText={errors?.code || " "}
                  value={formData?.code}
                  disabled={connectionKeyData?.code ? true : false}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
                <label className="label-text">
                  Type<span className="required-asterisk">*</span>
                </label>
                <Select
                  fullWidth
                  value={formData?.type} // Use dynamic state
                  onChange={handelOnChangeType} // Ensure correct handler
                  MenuProps={{ disableScrollLock: true }}
                  title="Type"
                  name="type"
                  style={{ width: "100%", height: 35 }}
                  className={`form-control-autocomplete form-control-autocomplete-1`}
                >
                  {Object.entries(CONNECTION_KEY_SERVICES_GROUPED).reduce(
                    (acc: any, [groupName, services]) => {
                      acc.push(
                        <ListSubheader
                          sx={{
                            fontSize: "15px",
                            fontWeight: "bold",
                            lineHeight: "32px",
                            backgroundColor: "#f3f3f3",
                          }}
                          key={`subheader-${groupName}`}
                        >
                          {groupName}
                        </ListSubheader>,
                      );
                      acc.push(
                        ...Object.entries(services).map(([key, value]) => (
                          <MenuItem
                            key={key}
                            value={key}
                            sx={{ paddingLeft: "24px" }}
                          >
                            <span style={{ textTransform: "capitalize" }}>
                              {value}
                            </span>
                          </MenuItem>
                        )),
                      );
                      return acc;
                    },
                    [],
                  )}
                </Select>
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
                <label className="label-text">
                  System<span className="required-asterisk">*</span>
                </label>
                <Autocomplete
                  fullWidth
                  options={allSystemData || []}
                  getOptionLabel={(option: any) => option.value || ""}
                  value={
                    allSystemData?.find(
                      (option: { id: any }) =>
                        option.id === formData?.system_id,
                    ) || null
                  }
                  renderInput={(params) => (
                    <TextField
                      name="system_id"
                      style={{ color: "#000000" }}
                      {...params}
                      placeholder="Select..."
                      InputLabelProps={{
                        shrink: true,
                      }}
                      error={!!errors?.system_id}
                      helperText={errors?.system_id || ""}
                    />
                  )}
                  loadingText="Loading..."
                  onChange={(event, value) => {
                    setFormData({
                      ...formData,
                      system_id: value?.id || null,
                    });
                    validateField("system_id", value?.id);
                  }}
                  className={`form-control-autocomplete ${
                    errors?.system_id ? "has-error" : ""
                  }`}
                />
              </Grid>
              {formData.type === "sftp" && sftpFields()}
              {[
                "mariadb",
                "azure_sql",
                "oracle_sql",
                "snowflake_sql",
                "postgres_sql",
              ].includes(formData.type) && sqlFields()}
              {formData.type === "blob" && blobFields()}
              {[
                "no_auth_api",
                "basic_auth_api",
                "token_auth_api",
                "key_auth_api",
                "oauth_api",
              ].includes(formData.type) && apiFields()}
              {["basic_auth_api"].includes(formData.type) && basicAuthFields()}

              {["token_auth_api"].includes(formData.type) && tokenAuthFields()}

              {["key_auth_api"].includes(formData.type) && keyAuthFields()}
              {formData.type === "azure_sql_service_principal" && (
                <>
                  {azureSqlServicePrincipalFields()}
                  {sqlFields()}
                </>
              )}
            </Grid>
          </Box>
          <Box>
            <FlexBetween
              gap={2.5}
              sx={{
                marginTop: "6px",
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              {formData?.type !== "local" && !connectionKeyId && (
                <Button
                  type="button"
                  className="btn-orange"
                  color="secondary"
                  variant="contained"
                  onClick={handleKeyTesting}
                >
                  <SaveOutlinedIcon /> &nbsp; Test Key
                </Button>
              )}
              <ViewJsonSplitButton
                onViewJson={() => setIsPayloadDialogOpen(true)}
                onCopyJson={handleCopyPayload}
              />
              <Button
                type="submit"
                className="btn-orange"
                color="secondary"
                variant="contained"
                disabled={
                  isEditMode
                    ? !hasChanges
                    : formData?.type !== "local" && !isKeyTested
                }
              >
                <SaveOutlinedIcon /> &nbsp; Save
              </Button>
            </FlexBetween>
          </Box>
        </form>
      </Box>

      <ViewPayloadDialog
        openDialog={isPayloadDialogOpen}
        dialogTitle="Connection Key JSON"
        payload={buildPayload()}
        handleDialogClose={() => {
          setIsPayloadDialogOpen(false);
          setValidationResponse(null);
        }}
        onValidate={handleValidatePayload}
        isValidating={isValidating}
        validationResponse={validationResponse}
      />
      <CommentBeforeUpdateDialog
        title={"Confirm updating Connection Key"}
        dialogContent={
          <div>
            <p className="m-0 mb-2">
              Add a note before updating the Connection Key
            </p>
            <textarea
              value={comment}
              className={`form-control-1 max-60`}
              onChange={(e) => handleCommentChange(e)}
            />
          </div>
        }
        openConfirmation={openCommentConfirmation}
        handleSaveComment={handleSaveComment}
        handleCancel={handleCancelComment}
      />
    </>
  );
};

export default AddConnectionKey;
